import React, {useCallback, useEffect, useRef, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {invalidateRef} from "../service/refsCache";
import {ChevronDownIcon, PlusIcon} from "./icons";

interface BrandOption {
    id: string;
    name: string;
}

interface BrandSearchSelectProps {
    brands: BrandOption[];
    value: string;
    onChange: (brandId: string) => void;
    onBrandsUpdate: (brands: BrandOption[]) => void;
    placeholder?: string;
    className?: string;
    /** Гардероб, в котором заводится бренд (см. entity.Brand — owner-scoped); по умолчанию — свой. */
    ownerId?: string;
    /** id поля ввода — чтобы внешний `<label htmlFor>` называл комбобокс. */
    inputId?: string;
}

const BrandSearchSelect: React.FC<BrandSearchSelectProps> = ({
    brands,
    value,
    onChange,
    onBrandsUpdate,
    placeholder = "Найти или создать бренд",
    className = "",
    ownerId,
    inputId
}) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedBrand = brands.find(b => b.id === value);
    const displayValue = open ? query : (selectedBrand?.name ?? '');

    const filtered = query.trim() === ''
        ? brands
        : brands.filter(b => b.name.toLowerCase().includes(query.toLowerCase()));
    const exactMatch = query.trim() && brands.some(b => b.name.toLowerCase() === query.trim().toLowerCase());
    const showCreate = query.trim() && !exactMatch && !creating;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCreate = useCallback(async () => {
        const name = query.trim();
        if (!name) return;
        setCreating(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<BrandOption>('/brands', {name}, {params: ownerId ? {ownerId} : undefined});
            invalidateRef('brands', ownerId ? {ownerId} : undefined);
            onBrandsUpdate([...brands.filter(b => b.id !== res.data.id), res.data].sort((a, b) => a.name.localeCompare(b.name)));
            onChange(res.data.id);
            setQuery('');
            setOpen(false);
        } catch (err) {
            console.error('Failed to create brand:', err);
        } finally {
            setCreating(false);
        }
    }, [query, brands, onChange, onBrandsUpdate, ownerId]);

    const handleSelect = useCallback((b: BrandOption) => {
        onChange(b.id);
        setQuery('');
        setOpen(false);
    }, [onChange]);

    const handleClear = useCallback(() => {
        onChange('');
        setQuery('');
        setOpen(false);
    }, [onChange]);

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div className="flex gap-1">
                <input
                    id={inputId}
                    type="text"
                    value={displayValue}
                    onChange={e => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    className="input-field flex-1"
                />
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="px-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700"
                >
                    <ChevronDownIcon className={`w-5 h-5 transition-transform ${open ? 'rotate-180' : ''}`}/>
                </button>
            </div>
            {open && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filtered.length === 0 && !showCreate && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            Нет брендов
                        </div>
                    )}
                    {filtered.map(b => (
                        <button
                            key={b.id}
                            type="button"
                            onClick={() => handleSelect(b)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${value === b.id ? 'bg-primary-50 dark:bg-primary-900/30' : ''}`}
                        >
                            {b.name}
                        </button>
                    ))}
                    {showCreate && (
                        <button
                            type="button"
                            onClick={handleCreate}
                            disabled={creating}
                            className="w-full text-left px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 border-t border-gray-200 dark:border-gray-600"
                        >
                            <PlusIcon className="w-4 h-4"/>
                            {creating ? 'Создание...' : `Создать «${query.trim()}»`}
                        </button>
                    )}
                    {value && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-600"
                        >
                            Очистить
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BrandSearchSelect;
